import { createTheme, PaletteMode } from '@mui/material/styles';

/**
 * The ShoutVector theme, light or dark (dark added 2026-10-17).
 *
 * Built by a function rather than exported once so the colour-mode switch in the top bar can
 * rebuild it. Only the palette and the input backgrounds differ between modes; type, shape and
 * components are shared. Components should take colours from the palette (text.secondary,
 * background.paper, divider, action.hover) rather than hex values, or they stay light in dark mode.
 */
export const makeTheme = (mode: PaletteMode) => createTheme({
  palette: {
    mode,
    primary: {
      main: '#1976d2', // Material blue
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#9c27b0', // Material purple
    },
    error: {
      main: '#ef4444',
      light: '#fecaca',
      dark: '#dc2626',
    },
    warning: {
      main: '#f59e0b',
      light: '#fef3c7',
      dark: '#d97706',
    },
    success: {
      main: '#10b981',
      light: '#d1fae5',
      dark: '#059669',
    },
    grey: {
      50: '#f9fafb',
      100: '#f3f4f6',
      200: '#e5e7eb',
      300: '#d1d5db',
      400: '#9ca3af',
      500: '#6b7280',
      600: '#4b5563',
      700: '#374151',
      800: '#1f2937',
      900: '#111827',
    },
    background: mode === 'dark'
      ? { default: '#0f172a', paper: '#1e293b' }
      : { default: '#f5f5f5', paper: '#ffffff' },
    text: mode === 'dark'
      ? { primary: '#f1f5f9', secondary: '#94a3b8' }
      : { primary: '#111827', secondary: '#6b7280' },
    ...(mode === 'dark' ? { divider: 'rgba(148, 163, 184, 0.16)' } : {}),
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: '2.25rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '1.875rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.125rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none', // Disable uppercase transformation for buttons
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8, // Consistent rounded corners
  },
  shadows: [
    'none',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: 8,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
          },
        },
      },
    },
    /**
     * Every input gets a white field.
     *
     * Outlined inputs are transparent by default, so on the grey page background they read as
     * grey panels rather than as something you can type in. Set at MuiOutlinedInput rather
     * than on MuiTextField because Select and the date pickers render the same component —
     * styling TextField alone would leave those two looking different.
     */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff',
          // Hover and focus inherit the base colour unless restated: MUI's own hover rule is
          // more specific, and without these the field greys out as soon as it is used.
          '&:hover': { backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff' },
          '&.Mui-focused': { backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff' },
          // A disabled field stays visibly disabled — white here would say "type in me".
          '&.Mui-disabled': { backgroundColor: mode === 'dark' ? '#1e293b' : '#f3f4f6' },
        },
      },
    },
    /** The same for filled and standard variants, in case either is used later. */
    MuiFilledInput: {
      styleOverrides: {
        root: {
          backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff',
          '&:hover': { backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff' },
          '&.Mui-focused': { backgroundColor: mode === 'dark' ? '#0f172a' : '#ffffff' },
          '&.Mui-disabled': { backgroundColor: mode === 'dark' ? '#1e293b' : '#f3f4f6' },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          fontWeight: 500,
        },
      },
    },
  },
});

/**
 * Every Export control is the header's blue, filled (Ian, 2026-10-18), so exporting looks the same
 * wherever it is offered. The header bar is `primary.main`; so are these.
 *
 * `gridExportSx` reaches into DataGrid's built-in toolbar, whose Export trigger is an icon button
 * labelled by its tooltip — there is no prop for its colour. Spread it into a DataGrid's `sx`.
 */
export const gridExportSx = {
  '& .MuiDataGrid-toolbar button[aria-label="Export"]': {
    bgcolor: 'primary.main',
    color: 'primary.contrastText',
    borderRadius: 1,
    '&:hover': { bgcolor: 'primary.dark' },
  },
} as const;

/** The light theme, for anything that imports a fixed theme rather than the switcher's. */
const theme = makeTheme('light');
export default theme;
